import {
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline'
import { useState } from 'react'

interface ExpandableJsonPanelProps {
  title?: string
  data: unknown
  defaultExpanded?: boolean
  maxHeight?: string
  className?: string
  containerClassName?: string
  headerClassName?: string
  preClassName?: string
  formatter?: (data: unknown) => string
}

const ExpandableJsonPanel = ({
  title = 'JSON Payload',
  data,
  defaultExpanded = false,
  maxHeight = 'max-h-96',
  className = '',
  containerClassName = '',
  headerClassName = '',
  preClassName = '',
  formatter,
}: ExpandableJsonPanelProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)
  const [isCopied, setIsCopied] = useState(false)

  const formattedContent = formatter
    ? formatter(data)
    : JSON.stringify(data || {}, null, 2)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(formattedContent)
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  return (
    <div className={containerClassName}>
      <div
        className={`flex items-center justify-between gap-2 mb-2 ${headerClassName}`}
      >
        <h4 className="text-sm font-semibold uppercase tracking-wide">
          {title}
        </h4>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="btn btn-ghost btn-xs flex items-center"
            onClick={handleCopy}
            title={isCopied ? 'Copied!' : 'Copy to clipboard'}
            aria-label="Copy content to clipboard"
          >
            {isCopied ? (
              <ClipboardDocumentCheckIcon className="w-4 h-4 text-success" />
            ) : (
              <ClipboardDocumentIcon className="w-4 h-4" />
            )}
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-xs flex items-center"
            onClick={() => setIsExpanded(!isExpanded)}
            title={isExpanded ? 'Collapse' : 'Expand to full height'}
            aria-label={isExpanded ? `Collapse ${title}` : `Expand ${title}`}
          >
            {isExpanded ? (
              <ChevronUpIcon className="w-4 h-4" />
            ) : (
              <ChevronDownIcon className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>
      <pre
        className={`mt-2 rounded-box bg-base-200 p-4 text-xs ${
          isExpanded ? 'h-full overflow-auto' : `${maxHeight} overflow-auto`
        } ${className} ${preClassName}`}
      >
        {formattedContent}
      </pre>
    </div>
  )
}

export { ExpandableJsonPanel }
